import { el, inject, awaitRedraw } from "@dhx/ts-common/dom";
import { isDefined, isEmptyObj } from "@dhx/ts-common/core";
import { baseProps, getValidationMessage, isVerify } from "../helper";
import { Combobox, ComboboxEvents } from "@dhx/ts-combobox";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { Label } from "./helper/label";
import {
	ValidationStatus,
	IComboConfig,
	ItemEvent,
	IComboEventHandlersMap,
	ICombo,
	IComboProps,
} from "../types";
import { Id } from "@dhx/ts-common/types";

export class Combo extends Label implements ICombo {
	public config: IComboConfig;
	public combobox: Combobox;
	public events: IEventSystem<ItemEvent, IComboEventHandlersMap> = new EventSystem();

	private _isValid = true;
	private _propsItem = [
		"required",
		"validation",
		"placeholder",
		"label",
		"labelWidth",
		"labelPosition",
		"hiddenLabel",
		"helpMessage",
		"preMessage",
		"successMessage",
		"errorMessage",
		"readonly",
		"readOnly",
	];
	private _propsCombo = [
		"template",
		"filter",
		"multiselection",
		"selectAllButton",
		"itemsCount",
		"itemHeight",
		"virtual",
		"listHeight",
	];
	private _props = [...baseProps, ...this._propsItem, ...this._propsCombo];

	constructor(container, config: IComboConfig) {
		super(null, config);

		this._initView(config);
		this._initHandlers();
	}
	destructor() {
		this.events && this.events.clear();
		this.combobox && this.combobox.destructor();

		this.events = this._uid = this._propsCombo = this._propsItem = this._props = this._isValid = null;
		super._destructor();
		this.unmount();
	}
	setProperties(propertyConfig: IComboProps): void {
		awaitRedraw().then(() => {
			if (
				!propertyConfig ||
				isEmptyObj(propertyConfig) ||
				!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
			)
				return;

			for (const key in propertyConfig) {
				if (this._props.includes(key)) {
					this.config[key] = propertyConfig[key];
				}
			}
			const data = [];
			this.getWidget().data.forEach(item => data.push(item));
			this.config.data = data;
			this._initView(this.config);
			this._initHandlers();

			this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
			this.combobox.paint();
			this.paint();
		});
	}
	getProperties(): IComboProps {
		const obj: IComboProps = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	show(): void {
		const { value, hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [value])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [value]);
	}
	hide(init?: boolean): void {
		const { value, hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [value, init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [value, init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.combobox.disable();
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this.combobox.enable();
		this.paint();
	}
	isDisabled(): boolean {
		return !!this.config.disabled;
	}
	clear(): void {
		if (!this.config.value) return;
		this.config.value = "";
		this.combobox.clear();
	}
	getValue(): Id | Id[] {
		if (this.config.multiselection) {
			return this.combobox.getValue(true) || [];
		} else {
			return this.combobox.getValue() || "";
		}
	}
	setValue(value: Id | Id[]): void {
		if (typeof value === "undefined" || value === this.config.value) return;
		value && this.combobox.setValue(value);
	}
	validate(silent = false, validateValue?: Id | Id[]): boolean {
		const value = typeof validateValue === "undefined" ? this.getValue() : validateValue;
		if (!silent && !this.events.fire(ItemEvent.beforeValidate, [value])) return false;

		let text: string | string[];

		if (Array.isArray(value)) {
			text = value.map(id => this._getItemText(id));
		} else {
			text = this._getItemText(value) || "";
		}

		this._isValid = this.config.validation
			? this.config.validation(value, text)
			: this._exsistData(value);

		if (!silent) {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.config.required && this._validationStatus();
			this.events.fire(ItemEvent.afterValidate, [value, this._isValid]);
		}

		return this._isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this._validationStatus();
		this.paint();
	}
	getWidget(): Combobox {
		return this.combobox;
	}
	focus(): void {
		awaitRedraw().then(() => {
			this.combobox.focus();
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			this.combobox.blur();
		});
	}
	protected _initView(config: IComboConfig): void {
		if (isEmptyObj(config)) {
			throw new Error("Check the configuration is correct");
		}
		if (this.combobox) {
			this.combobox.destructor();
		}
		this.config = {
			type: config.type,
			id: config.id,
			name: config.name,
			disabled: false,
			hidden: false,
			value: "",
			readOnly: false,
			multiselection: false,
			selectAllButton: false,
			itemHeight: 32,
			virtual: false,
			listHeight: 224,
			required: false,
			placeholder: "",
			label: "",
			labelWidth: "",
			labelPosition: "top",
			hiddenLabel: false,
			helpMessage: "",
			preMessage: "",
			successMessage: "",
			errorMessage: "",
			width: "content",
			height: "content",
		};
		const comboConfig = {};
		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
				if (key !== "validation") {
					comboConfig[key] = config[key];
				}
			}
		}

		this.combobox = new Combobox(null, comboConfig);

		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}
		this.paint();
	}
	protected _initHandlers() {
		this.combobox.events.on(ComboboxEvents.change, () => {
			const value = (this.config.value = this.getValue());
			this.events.fire(ItemEvent.change, [value]);
			isVerify(this.config) && this.validate();
			this.paint();
		});
		this.combobox.events.on(ComboboxEvents.focus, () => {
			this.events.fire(ItemEvent.focus, [this.getValue()]);
			this.paint();
		});
		this.combobox.events.on(ComboboxEvents.blur, () => {
			this.events.fire(ItemEvent.blur, [this.getValue()]);
			this.paint();
		});
		this.combobox.events.on(ComboboxEvents.keydown, (event: KeyboardEvent, id: string | undefined) => {
			if (event.code === "Enter") {
				event.preventDefault();
			}
			this.events.fire(ItemEvent.keydown, [event, id]);
		});
		this.events.on(ItemEvent.afterValidate, () => {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.paint();
		});
	}
	protected _validationStatus(): any {
		const activeFocus =
			this.combobox.popup.isVisible() ||
			this.combobox.getRootView()?.refs?.input?.el === document.activeElement;
		if (!activeFocus) {
			switch (this.config.$validationStatus) {
				case ValidationStatus.success:
					return (this.combobox.config.css =
						(this.config.css || "") + "dhx_form-group--state_success");
				case ValidationStatus.error:
					return (this.combobox.config.css =
						(this.config.css || "") + "dhx_form-group--state_error");
				case ValidationStatus.pre:
				default:
					return (this.combobox.config.css = this.config.css || "");
			}
		}
		return (this.combobox.config.css = this.config.css || "");
	}
	protected _getRootView(): any {
		return this.combobox.getRootView();
	}
	protected _draw() {
		const { labelWidth, labelPosition, name, id } = this.config;

		return el(
			".dhx_form-group",
			{
				"data-dhx-id": name || id,
			},
			[
				inject(this._getRootView()),
				el(
					"div",
					{
						style: {
							"margin-left": `${
								labelWidth && labelPosition === "left" ? `calc(${labelWidth} + 16px)` : ""
							}`,
						},
						class: this._validationStatus(),
					},
					[el("span.dhx_input__caption", getValidationMessage(this.config))]
				),
			]
		);
	}
	private _exsistData(id: Id | Id[]): boolean {
		if (!isDefined(id)) return false;

		if (id instanceof Array) {
			if (!id.length) return false;

			return id.every(i => {
				return this.combobox.data.exists(i);
			});
		}
		return this.combobox.data.exists(id);
	}
	private _getItemText(id: Id): string {
		return this.combobox.data.getItem(id)?.value;
	}
}
